/**
 * Shared bill PDF generator - used by PartyManagement and BillManagement.
 * Generates PDF client-side, uploads to backend, updates bill with pdfFile.
 */
import html2pdf from "html2pdf.js";
import axios from "axios";
import billService from "./billService";
import businessService from "./businessService";
import whatsappApi from "./api";

const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (num === 0) return 'Zero';

  const convertHundreds = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) {
      const ten = Math.floor(n / 10);
      const one = n % 10;
      return tens[ten] + (one > 0 ? ' ' + ones[one] : '');
    }
    const hundred = Math.floor(n / 100);
    const remainder = n % 100;
    return ones[hundred] + ' Hundred' + (remainder > 0 ? ' ' + convertHundreds(remainder) : '');
  };

  if (num < 1000) return convertHundreds(num);

  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const remainder = num % 1000;

  let result = '';
  if (crore > 0) result += convertHundreds(crore) + ' Crore ';
  if (lakh > 0) result += convertHundreds(lakh) + ' Lakh ';
  if (thousand > 0) result += convertHundreds(thousand) + ' Thousand ';
  if (remainder > 0) result += convertHundreds(remainder);

  return result.trim() + ' Only';
};

const KACHHA_BILL_ROWS = 10;

/** Get base URL for /uploads - empty in dev (proxy handles same-origin); use REACT_APP_BACKEND_URL on VPS */
const getUploadBaseUrl = () => {
  const env = typeof process !== 'undefined' && process.env?.REACT_APP_BACKEND_URL;
  if (env) return env.replace(/\/$/, '');
  return ''; // relative URL - proxy forwards /uploads to backend in dev
};

/** Fetch image URL and return as base64 data URL (for PDF rendering) */
const fetchImageAsDataUrl = async (url) => {
  if (!url) return null;
  try {
    const base = getUploadBaseUrl();
    const fullUrl = url.startsWith('http') ? url : (base + (url.startsWith('/') ? url : '/' + url));
    const res = await axios.get(fullUrl, { responseType: 'arraybuffer', timeout: 5000, withCredentials: true });
    const base64 = btoa(new Uint8Array(res.data).reduce((acc, b) => acc + String.fromCharCode(b), ''));
    const type = res.headers['content-type'] || 'image/png';
    return `data:${type};base64,${base64}`;
  } catch (e) {
    console.warn('Signature image fetch failed:', e?.message);
    return null;
  }
};

const getKachhaBillHTML = (bill, business, options = {}) => {
  const { gstIncluded = false, signatureDataUrl } = options;
  const escapeHtml = (t) => !t ? '' : String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  const billDate = new Date(bill.billDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  const formatCurrency = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const companyName = escapeHtml((business?.companyName && String(business.companyName).toLowerCase() !== 'test') ? business.companyName : "RAHUL TRADERS");
  const rawLogoUrl = business?.companyLogo?.url || '';
  const companyLogoUrl = rawLogoUrl && !rawLogoUrl.startsWith('http') && typeof window !== 'undefined'
    ? window.location.origin + (rawLogoUrl.startsWith('/') ? '' : '/') + rawLogoUrl
    : rawLogoUrl;
  const bankName = escapeHtml(business?.bankName || '');
  const accountNumber = escapeHtml(business?.accountNumber || '');
  const ifscCode = escapeHtml(business?.ifscCode || '');
  const accountHolder = escapeHtml(business?.accountHolderName || business?.companyName || '');
  const footerCompany = business?.companyName || '';
  const hasBankDetails = !!(business?.bankName && business?.accountNumber && business?.ifscCode);
  const customerName = escapeHtml(bill.billingName || bill.party?.companyName || bill.party?.partyName || "Customer");
  const grandTotal = (bill.items || []).reduce((sum, item) => sum + (item.quantity || 0) * (item.rate || 0), 0);

  const items = bill.items || [];
  const dataRows = items.map((item, index) => {
    const srNo = index + 1;
    const name = escapeHtml(item.productName || "");
    const qty = item.quantity || 0;
    const price = formatCurrency(item.rate || 0);
    return `<tr><td class="tc">${srNo}</td><td class="tl">${name}</td><td class="tr">${qty}</td><td class="tr">${price}</td></tr>`;
  });
  const rows = dataRows.join("");

  const billTypeLabel = gstIncluded ? "Kachha Bill (With GST)" : "Kachha Bill (Without GST)";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>Kachha Bill</title>
  <style>
    * { box-sizing: border-box; }
    @page { size: A4; margin: 12mm; }
    body { margin: 0; padding: 0; background: #fff; font-family: Arial, sans-serif; font-size: 14px; display: flex; justify-content: center; }
    .main { position: relative; width: 794px; max-width: 100%; margin: 0 auto; padding: 57px; background: #fff; }
    thead { display: table-header-group; }
    .bill-table tbody tr { break-inside: avoid; page-break-inside: avoid; }
    .kachha-footer-table { break-inside: avoid; page-break-inside: avoid; }
    .company-header-row { display: flex; align-items: center; justify-content: center; margin-bottom: 24px; gap: 16px; }
    .company-header-row .logo-cell { flex-shrink: 0; }
    .company-header-row .info-cell { flex: 1; text-align: center; }
    .company-name { font-size: 22px; font-weight: bold; }
    .company-name.only { text-align: center; margin-bottom: 24px; }
    .bill-info { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; font-size: 13px; padding: 0 4px; }
    .bill-type-line { text-align: center; font-size: 14px; font-weight: bold; margin-bottom: 16px; padding: 6px 0; border-bottom: 1px solid #222; }
    .bill-table { width: 100%; border-collapse: collapse; margin-top: 0; table-layout: fixed; }
    .bill-table th, .bill-table td { border: 1px solid #222; padding: 8px 12px; }
    .bill-table th { background: #e0e0e0; font-weight: bold; }
    .tl { text-align: left; } .tc { text-align: center; } .tr { text-align: right; }
    .grand-total-wrap { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; padding-top: 12px; border-top: 1px solid #222; }
    .grand-total { font-size: 16px; font-weight: bold; text-align: right; }
    .company-logo { max-height: 56px; max-width: 120px; display: block; object-fit: contain; }
    .kachha-footer-table { width: 100%; border-collapse: collapse; border-spacing: 0; margin-top: 20px; table-layout: fixed; border-bottom: 1px solid #222; }
    .kachha-footer-table td { width: 33.33%; border-top: 1px solid #222; border-right: 1px solid #222; padding: 10px; font-size: 12px; line-height: 1.45; vertical-align: top; box-sizing: border-box; }
    .kachha-footer-table td:last-child { border-right: none; }
    .kachha-footer-td-sign { text-align: center; }
    .auth-signatory { font-size: 11px; margin-top: 4px; }
.footer-signature-img { max-height: 48px; max-width: 140px; object-fit: contain; display: block; margin: 4px auto; }
.signature-line { border-bottom: 1px solid #000; width: 120px; margin: 6px auto 4px; height: 0; line-height: 0; }
  </style>
</head>
<body>
  <div class="main">
    ${companyLogoUrl
      ? `<div class="company-header-row"><div class="logo-cell"><img src="${companyLogoUrl.replace(/"/g, '&quot;')}" alt="Logo" class="company-logo"/></div><div class="info-cell"><div class="company-name">${companyName}</div></div></div>`
      : `<div class="company-name only">${companyName}</div>`
    }
    <div class="bill-type-line">${billTypeLabel}</div>
    <div class="bill-info">
      <span><strong>Date:</strong> ${billDate}</span>
      <span><strong>Customer:</strong> ${customerName}</span>
    </div>
    ${items.length > 0 ? `<table class="bill-table">
      <thead><tr><th class="tc">Sr No</th><th class="tl">Name</th><th class="tr">Quantity</th><th class="tr">Price</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="grand-total-wrap"><span></span><span class="grand-total">Grand Total: ${formatCurrency(grandTotal)}</span></div>` : ''}
    <table class="kachha-footer-table" role="presentation"><tr>
${hasBankDetails ? `<td><b>Bank Details</b><br>Name: ${bankName}<br>Account No.: ${accountNumber}<br>IFSC code: ${ifscCode}<br>Account Holder's Name: ${accountHolder}</td>` : '<td></td>'}
<td><b>Terms and conditions</b><br>1. Subject to Ahmedabad Jurisdiction Only.</td>
<td class="kachha-footer-td-sign">${footerCompany ? `For: <b>${footerCompany}</b><br>` : ""}${signatureDataUrl ? `<img src="${signatureDataUrl.replace(/"/g, '&quot;')}" alt="Signature" class="footer-signature-img"/>` : ""}<div class="signature-line"></div><div class="auth-signatory">Authorized Signatory</div></td>
</tr></table>
    ${gstIncluded ? '<p class="bill-note">Price Inclusive of GST</p>' : '<p class="bill-note">Without GST</p>'}
  </div>
</body>
</html>`;
};

// Generate HTML invoice - shared with BillManagement preview
export function generateInvoiceHTML(bill, business, options = {}) {
  const { signatureDataUrl } = options;
  const billType = bill.billType || "withoutTax";
  const isKachhaNoGst = billType === "kachuBill" || billType === "delivery";
  const isKachhaGstIncl = billType === "kachuBillGstIncl";
  const isDelivery = isKachhaNoGst || isKachhaGstIncl;
  const isWithTax = billType === "withTax";
  const isIgst = billType === "withTaxIgst" || billType === "withoutTaxIgst";

  const escapeHtml = (text) => {
    if (!text) return '';
    return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  };

  const billDate = new Date(bill.billDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

  if (isKachhaNoGst) return getKachhaBillHTML(bill, business, { gstIncluded: false, signatureDataUrl });
  if (isKachhaGstIncl) return getKachhaBillHTML(bill, business, { gstIncluded: true, signatureDataUrl });

  const billNumberParts = (bill.billNumber || '').split('_');
  const invoiceNumber = billNumberParts.length > 1 ? billNumberParts[billNumberParts.length - 2] : (bill.billNumber || '');

  const placeOfSupply = business?.state ? `${business.state.split('-')[0] || '24'}-${business.state.split('-')[1] || 'Gujarat'}` : '24-Gujarat';

  const gstSummary = {};
  const itemsList = bill.items || [];
  if (!isDelivery) {
    itemsList.forEach((item) => {
      const rawHsn = item.hsnCode != null && String(item.hsnCode).trim() !== "" ? String(item.hsnCode).trim() : "N/A";
      const gstRate = item.gstRate || 0;
      const key = `${rawHsn}_${gstRate}`;
      const hsn = rawHsn;
      if (!gstSummary[key]) {
        const cgstRate = gstRate / 2;
        const sgstRate = gstRate / 2;
        gstSummary[key] = { hsn, gstRate, taxable: 0, cgstRate, sgstRate, cgstAmount: 0, sgstAmount: 0, igstRate: gstRate, igstAmount: 0, totalTax: 0 };
      }
      const taxable = item.amount || 0;
      const s = gstSummary[key];
      s.taxable += taxable;
      s.cgstAmount += (taxable * s.cgstRate) / 100;
      s.sgstAmount += (taxable * s.sgstRate) / 100;
      s.igstAmount += (taxable * s.igstRate) / 100;
      s.totalTax += (taxable * s.igstRate) / 100;
    });
  }

  const gstSummaryRowsSorted = !isDelivery
    ? Object.values(gstSummary).sort((a, b) => {
        const h = String(a.hsn).localeCompare(String(b.hsn), undefined, { numeric: true });
        return h !== 0 ? h : (a.gstRate || 0) - (b.gstRate || 0);
      })
    : [];
  const gstSummaryRows = !isDelivery && !isIgst
    ? gstSummaryRowsSorted
        .map(
          (s) =>
            `<tr class="hsn-body-row"><td class="hsn-code">${s.hsn}</td><td class="hsn-amt">${formatCurrency(s.taxable)}</td><td class="hsn-rate">${s.cgstRate.toFixed(2)}%</td><td class="hsn-amt">${formatCurrency(s.cgstAmount)}</td><td class="hsn-rate">${s.sgstRate.toFixed(2)}%</td><td class="hsn-amt">${formatCurrency(s.sgstAmount)}</td><td class="hsn-amt">${formatCurrency(s.totalTax)}</td></tr>`
        )
        .join("")
    : "";
  const gstSummaryRowsIgst = !isDelivery && isIgst
    ? gstSummaryRowsSorted
        .map(
          (s) =>
            `<tr class="hsn-body-row"><td class="hsn-code">${s.hsn}</td><td class="hsn-amt">${formatCurrency(s.taxable)}</td><td class="hsn-rate">${s.igstRate.toFixed(2)}%</td><td class="hsn-amt">${formatCurrency(s.igstAmount)}</td><td class="hsn-amt">${formatCurrency(s.totalTax)}</td></tr>`
        )
        .join("")
    : "";
  const totalCgst = !isDelivery && !isIgst ? Object.values(gstSummary).reduce((sum, s) => sum + s.cgstAmount, 0) : 0;
  const totalSgst = !isDelivery && !isIgst ? Object.values(gstSummary).reduce((sum, s) => sum + s.sgstAmount, 0) : 0;

  const companyName = escapeHtml((business?.companyName && String(business.companyName).toLowerCase() !== 'test') ? business.companyName : 'RAHUL TRADERS');
  const companyAddress = escapeHtml(business?.address || '');
  const companyCity = business?.city ? escapeHtml(`${business.city} - ${business.pincode || ''}`) : '';
  const companyPhone = business?.phone ? `Phone: ${escapeHtml(business.phone)}` : '';
  const companyGstin = !isDelivery && business?.gstin ? `GSTIN: ${escapeHtml(business.gstin)}` : '';
  const partyData = bill.party || bill.partyId || {};
  const formatAddressForBill = (address) => {
    const raw = address == null ? "" : String(address).trim();
    if (!raw) return "";
    const chunks = raw.split(/\s*,\s*/).map((p) => p.trim()).filter(Boolean);
    if (chunks.length >= 2) {
      const mid = Math.ceil(chunks.length / 2);
      const line1 = chunks.slice(0, mid).join(", ");
      const line2 = chunks.slice(mid).join(", ");
      return `${escapeHtml(line1)}<br/>${escapeHtml(line2)}`;
    }
    const text = chunks[0] || raw;
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length <= 1) return escapeHtml(text);
    const mid = Math.ceil(words.length / 2);
    const line1 = words.slice(0, mid).join(" ");
    const line2 = words.slice(mid).join(" ");
    return `${escapeHtml(line1)}<br/>${escapeHtml(line2)}`;
  };
  const partyName = escapeHtml(bill.billingName || partyData.companyName || partyData.partyName || "");
  const partyAddress = formatAddressForBill(partyData.address || "");
  const partyGstin = !isDelivery && partyData.gstin ? `GSTIN: ${escapeHtml(partyData.gstin)}` : "";
  const bankName = escapeHtml(business?.bankName || '');
  const accountNumber = escapeHtml(business?.accountNumber || '');
  const ifscCode = escapeHtml(business?.ifscCode || '');
  const accountHolder = escapeHtml(business?.accountHolderName || business?.companyName || '');
  const footerCompany = business?.companyName || '';
  const hasBankDetails = !!(business?.bankName && business?.accountNumber && business?.ifscCode);
  const rawLogoUrl = business?.companyLogo?.url || '';
  const companyLogoUrl = rawLogoUrl && !rawLogoUrl.startsWith('http') && typeof window !== 'undefined'
    ? window.location.origin + (rawLogoUrl.startsWith('/') ? '' : '/') + rawLogoUrl
    : rawLogoUrl;

  const itemCount = itemsList.length;
  const totalQuantity = itemsList.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const priceUnitLabel = !isDelivery && (isWithTax || isIgst) ? "Price/ Unit (incl. GST)" : "Price/ Unit";
  const itemsTableHeader = isDelivery
    ? `<tr class="center bold"><td>Sr. No.</td><td>Item name</td><td>Quantity</td><td>Unit</td><td>${priceUnitLabel}</td><td>Amount</td></tr>`
    : `<tr class="center bold"><td>Sr. No.</td><td>Item name</td><td>HSN/ SAC</td><td>Quantity</td><td>Unit</td><td>${priceUnitLabel}</td><td>Taxable amount</td><td>GST</td><td>Amount</td></tr>`;

  const itemsTotalRow = itemCount > 0
    ? (isDelivery
        ? `<tr class="bold"><td></td><td class="left">Total</td><td class="center">${totalQuantity}</td><td></td><td></td><td class="right">${formatCurrency(bill.grandTotal)}</td></tr>`
        : `<tr class="bold"><td></td><td class="left">Total</td><td></td><td class="center">${totalQuantity}</td><td></td><td></td><td class="right">${formatCurrency(bill.subtotal)}</td><td class="right">${formatCurrency(bill.totalGst)}</td><td class="right">${formatCurrency(bill.grandTotal)}</td></tr>`)
    : '';

  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.max(1, Math.ceil(itemCount / ITEMS_PER_PAGE));
  const useAutoHeight = itemCount > 0 || totalPages > 1;
  const colCount = isDelivery ? 6 : 9;
  const spacerCells = Array(colCount).fill('<td class="items-table-spacer-cell">&nbsp;</td>').join('');
  const spacerRow = `<tr class="items-table-spacer">${spacerCells}</tr>`;
  /* Fewer filler rows for PDF — tall blank blocks steal space and push HSN/footer across page breaks. */
  const ITEM_ROWS_DISPLAY = 15;
  const blankRowsCount = totalPages === 1 && itemCount > 0 && itemCount < ITEM_ROWS_DISPLAY ? ITEM_ROWS_DISPLAY - itemCount : 0;
  const blankBlockHeightPx = blankRowsCount * 18;
  const blankBlockCells = Array(colCount).fill(`<td class="items-table-blank-block-cell" style="height:${blankBlockHeightPx}px">&nbsp;</td>`).join('');
  const blankRowsHtml = blankRowsCount > 0 ? `<tr class="items-table-blank-block">${blankBlockCells}</tr>` : '';

  const makeItemRow = (item, srNo) => {
    const taxable = item.amount || 0;
    const gst = item.gstAmount || 0;
    const total = taxable + gst;
    if (isDelivery) {
      return `<tr class="center"><td>${srNo}</td><td>${escapeHtml(item.productName || '')}</td><td>${item.quantity || 0}</td><td>${escapeHtml(item.unit || '')}</td><td>${formatCurrency(item.rate || 0)}</td><td>${formatCurrency(total)}</td></tr>`;
    }
    const gstRate = item.gstRate != null ? item.gstRate : 0;
    const gstCell = `${formatCurrency(gst)}<br><span class="small">(${gstRate.toFixed(1)}%)</span>`;
    return `<tr class="center"><td>${srNo}</td><td>${escapeHtml(item.productName || '')}</td><td>${escapeHtml(item.hsnCode || '')}</td><td>${item.quantity || 0}</td><td>${escapeHtml(item.unit || '')}</td><td>${formatCurrency(item.rate || 0)}</td><td>${formatCurrency(taxable)}</td><td>${gstCell}</td><td>${formatCurrency(total)}</td></tr>`;
  };

  const buildItemsTableForChunk = (chunk, showTotal, pageIndex, summaryRows = '', blankRows = '') => {
    const rowsHtml = chunk.map((item, i) => makeItemRow(item, pageIndex * ITEMS_PER_PAGE + i + 1)).join('');
    const addSpacer = !useAutoHeight && showTotal;
    return `<div class="section section--items"><div class="items-table-wrap ${useAutoHeight ? 'items-table-wrap--auto' : ''}">
<table class="items-table items-table--invoice">
<thead>${itemsTableHeader}</thead>
<tbody>${rowsHtml}${blankRows}${addSpacer ? spacerRow : ''}</tbody>
${showTotal ? `<tfoot class="items-tfoot-summary total-section">${itemsTotalRow}${summaryRows}</tfoot>` : ""}
</table>
</div></div>`;
  };

  const paidAmount = bill.paidAmount != null ? bill.paidAmount : 0;
  const balance = (bill.grandTotal || 0) - paidAmount;
  const leftColspan = colCount === 6 ? 2 : 4;
  const rightColspan = colCount === 6 ? 4 : 5;
  const amountWords = numberToWords(Math.round(bill.grandTotal));
  const summaryRowsHtml = `<tr class="amounts-summary-row">
<td colspan="${leftColspan}" class="left summary-quad summary-quad--words"><b>Invoice Amount In Words</b><br><b>${amountWords}</b></td>
<td colspan="${rightColspan}" class="amounts-cell summary-quad summary-quad--amounts"><b>Amounts</b><br>
<table class="no-border summary-amounts-mini" style="width:100%;">
<tr><td class="left bold">Total:</td><td class="right bold">${formatCurrency(bill.grandTotal)}</td></tr>
</table>
</td>
</tr>
<tr class="amounts-summary-row">
<td colspan="${leftColspan}" class="left payment-mode-cell summary-quad summary-quad--pay"><b>Payment Mode</b><br>Credit</td>
<td colspan="${rightColspan}" class="amounts-cell summary-quad summary-quad--recv"><table class="no-border summary-amounts-mini" style="width:100%;"><tr><td class="left">Received:</td><td class="right">${formatCurrency(paidAmount)}</td></tr><tr><td class="left bold">Balance:</td><td class="right bold">${formatCurrency(balance)}</td></tr></table></td>
</tr>`;

  const headerBlock = `<div class="section section--header">${!isDelivery ? '<table><tr><td class="tax-invoice-title">Tax Invoice</td></tr></table>' : ''}
<table>
  <tr>
    <td rowspan="4" width="55%">
      ${companyLogoUrl ? `<div class="company-header-with-logo"><div class="logo-cell"><img src="${companyLogoUrl.replace(/"/g, '&quot;')}" alt="Logo" class="company-logo"/></div><div class="info-cell bold">${companyName}<br>${companyAddress}<br>${companyCity}<br>${companyPhone}<br>${companyGstin}</div></div>` : `<span class="bold">${companyName}<br>${companyAddress}<br>${companyCity}<br>${companyPhone}<br>${companyGstin}</span>`}
    </td>
    <td width="22%">Invoice No:<br><b>${invoiceNumber}</b></td>
    <td width="23%">Date:<br><b>${billDate}</b></td>
  </tr>
  <tr><td>Place of Supply:<br><b>${placeOfSupply}</b></td><td>Transport Name:</td></tr>
  <tr><td>Vehicle Number:</td><td>Delivery Date:</td></tr>
  <tr><td>Delivery Location:</td><td>LR Number:</td></tr>
</table>

<table style="border-collapse: collapse;">
  <tr>
    <td class="bold" style="font-size:13px; padding-bottom:1px;">
      Bill To
    </td>
  </tr>
  <tr>
    <td style="font-size:13px; line-height:1.4; padding-top:1px;">
      <b style="font-size:13px; display:block; margin-bottom:1px;">
        ${partyName}
      </b>
      <span style="display:block; margin-bottom:1px;">
        ${partyAddress}
      </span>
      <span style="display:block;">
        ${partyGstin}
      </span>
    </td>
  </tr>
</table></div>`;

  const amountsBlock = `<div class="section section--totals"><table class="amounts-section">
  <tr>
    <td width="50%" class="left summary-quad summary-quad--words"><b>Invoice Amount In Words</b><br><b>${amountWords}</b></td>
    <td width="50%" class="amounts-cell summary-quad summary-quad--amounts"><b>Amounts</b><br>
      <table class="no-border summary-amounts-mini" style="width:100%;">
        <tr><td class="left bold">Total:</td><td class="right bold">${formatCurrency(bill.grandTotal)}</td></tr>
      </table>
    </td>
  </tr>
  <tr>
    <td class="left payment-mode-cell summary-quad summary-quad--pay"><b>Payment Mode</b><br>Credit</td>
    <td class="amounts-cell summary-quad summary-quad--recv"><table class="no-border summary-amounts-mini" style="width:100%;"><tr><td class="left">Received:</td><td class="right">${formatCurrency(paidAmount)}</td></tr><tr><td class="left bold">Balance:</td><td class="right bold">${formatCurrency(balance)}</td></tr></table></td>
  </tr>
</table></div>`;

  const gstBlockInner = !isDelivery && isIgst
    ? `<table class="gst-summary-table hsn-table hsn-table--igst" role="presentation">
<colgroup>
<col style="width:14%" /><col style="width:18%" /><col style="width:12%" /><col style="width:18%" /><col style="width:18%" />
</colgroup>
<thead>
<tr>
<th class="hsn-th-code">HSN/SAC</th>
<th class="hsn-th-amt">Taxable</th>
<th class="hsn-th-rate">IGST Rate</th>
<th class="hsn-th-amt">IGST Amt</th>
<th class="hsn-th-amt">Total Tax</th>
</tr>
</thead>
<tbody>${gstSummaryRowsIgst}</tbody>
<tfoot>
<tr class="gst-total-row total-row">
<td class="total-label">Total</td>
<td class="hsn-amt">${formatCurrency(bill.subtotal)}</td>
<td class="hsn-rate">&nbsp;</td>
<td class="hsn-amt">${formatCurrency(bill.totalGst)}</td>
<td class="hsn-amt">${formatCurrency(bill.totalGst)}</td>
</tr>
</tfoot>
</table>`
    : !isDelivery
      ? `<table class="gst-summary-table hsn-table hsn-table--cgst" role="presentation">
<colgroup>
<col style="width:11%" /><col style="width:14%" /><col style="width:8%" /><col style="width:13%" /><col style="width:8%" /><col style="width:13%" /><col style="width:13%" />
</colgroup>
<thead>
<tr>
<th rowspan="2" class="hsn-th-code">HSN/SAC</th>
<th rowspan="2" class="hsn-th-amt">Taxable</th>
<th colspan="2" class="hsn-th-group">CGST</th>
<th colspan="2" class="hsn-th-group">SGST</th>
<th rowspan="2" class="hsn-th-amt">Total Tax</th>
</tr>
<tr>
<th class="hsn-th-sub">Rate</th>
<th class="hsn-th-sub">Amount</th>
<th class="hsn-th-sub">Rate</th>
<th class="hsn-th-sub">Amount</th>
</tr>
</thead>
<tbody>${gstSummaryRows}</tbody>
<tfoot>
<tr class="gst-total-row total-row">
<td class="total-label">Total</td>
<td class="hsn-amt">${formatCurrency(bill.subtotal)}</td>
<td class="hsn-rate">&nbsp;</td>
<td class="hsn-amt">${formatCurrency(totalCgst)}</td>
<td class="hsn-rate">&nbsp;</td>
<td class="hsn-amt">${formatCurrency(totalSgst)}</td>
<td class="hsn-amt">${formatCurrency(bill.totalGst)}</td>
</tr>
</tfoot>
</table>`
      : "";

  const gstBlock = gstBlockInner ? `<div class="section section--gst">${gstBlockInner}</div>` : "";

  const footerBlock = `<div class="section section--footer footer-section"><table class="invoice-footer-table" role="presentation"><tr>
${hasBankDetails ? `<td class="invoice-footer-td"><b>Bank Details</b><br>Name: ${bankName}<br>Account No.: ${accountNumber}<br>IFSC code: ${ifscCode}<br>Account Holder's Name: ${accountHolder}</td>` : '<td class="invoice-footer-td"></td>'}
<td class="invoice-footer-td"><b>Terms and conditions</b><br>1. Subject to Ahmedabad Jurisdiction Only.</td>
<td class="invoice-footer-td invoice-footer-td-sign"><div class="footer-for-company">${footerCompany ? `For: <b>${footerCompany}</b>` : ""}</div>${signatureDataUrl ? `<img src="${signatureDataUrl.replace(/"/g, '&quot;')}" alt="Signature" class="footer-signature-img"/>` : ""}<div class="signature-line"></div><div class="auth-signatory">Authorized Signatory</div></td>
</tr></table></div>`;

  const bottomSectionHtml = `<div class="bottom-section"><div class="gst-footer-cluster">${gstBlock}${footerBlock}</div></div>`;

  let mainContent;
  if (itemCount === 0) {
    mainContent = headerBlock + amountsBlock + bottomSectionHtml;
  } else if (totalPages === 1) {
    const tableHtml = buildItemsTableForChunk(itemsList, true, 0, summaryRowsHtml, blankRowsHtml);
    mainContent = headerBlock + tableHtml + bottomSectionHtml;
  } else {
    const pages = [];
    for (let p = 0; p < totalPages; p++) {
      const start = p * ITEMS_PER_PAGE;
      const chunk = itemsList.slice(start, start + ITEMS_PER_PAGE);
      const isLast = p === totalPages - 1;
      const tableHtml = buildItemsTableForChunk(chunk, isLast, p, isLast ? summaryRowsHtml : "", "");
      const below = isLast ? bottomSectionHtml : '<div class="continued-msg">Continued on next page…</div>';
      pages.push(`<div class="invoice-page">${headerBlock}${tableHtml}${below}</div>`);
    }
    mainContent = pages.join("");
  }

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Invoice</title>
<style>
/* Puppeteer / Chromium PDF print — A4, stable page breaks */
@page { size: A4; margin: 12mm; }
* { box-sizing: border-box; }
html {
  color-scheme: only light;
}
html, body {
  margin: 0;
  padding: 0;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 12px;
  line-height: 1.35;
  width: 100%;
  background: #fff !important;
  color: #000 !important;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
body { display: block; }
p { margin: 2px 0; }

.main {
  position: relative;
  width: 100%;
  max-width: 210mm;
  margin: 0 auto;
  border: 1px solid #000;
  box-sizing: border-box;
  padding: 0;
  overflow: visible;
}
.main > table { margin: 0; }

/* Section wrappers: keep totals / GST / footer intact; allow items table to span pages */
.section {
  break-inside: auto;
  page-break-inside: auto;
}
.section--header {
  break-inside: avoid;
  page-break-inside: avoid;
}
.section--items {
  break-inside: auto;
  page-break-inside: auto;
}
.section--totals {
  break-inside: avoid;
  page-break-inside: avoid;
}
/* Footer is inside .bottom-section — avoid duplicate break rules fighting */
.section--footer {
  margin: 0;
  padding: 0;
}
.section--gst {
  margin: 0;
  padding: 0;
}

/*
 * Allow HSN + footer to flow on the same page as the totals block when space allows.
 * break-inside: avoid on this wrapper forced the whole block to the next page whenever it did not fit
 * in the leftover strip — leaving a large empty gap on page 1 (html2pdf + Chromium fragmentation).
 * Rows stay intact via global tr rules; avoid: ['table'] stays off so HSN bodies are not clipped.
 */
.bottom-section {
  break-inside: auto;
  page-break-inside: auto;
  overflow: visible;
}
/* Keep HSN summary + bank/signature one unit so html2pdf does not insert a pad between them. */
.gst-footer-cluster {
  break-inside: avoid-page;
  page-break-inside: avoid;
  overflow: visible;
}
.total-section {
  break-inside: avoid;
  page-break-inside: avoid;
}
.gst-summary-table.hsn-table {
  break-inside: auto;
  page-break-inside: auto;
  overflow: visible;
}

table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
}
td, th { border: 1px solid #000; padding: 5px 6px; vertical-align: middle; background: #fff; }
td.amount,
.amount {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.no-border td { border: none; }
.center { text-align: center; }
.left { text-align: left; }
.right { text-align: right; }
.bold { font-weight: bold; }
.small { font-size: 11px; }

thead { display: table-header-group; }
tfoot { display: table-footer-group; }

tr {
  break-inside: avoid;
  page-break-inside: avoid;
}

.items-table--invoice tbody tr,
.items-table--invoice thead tr {
  break-inside: avoid;
  page-break-inside: avoid;
}
.hsn-table thead tr,
.hsn-table tbody tr,
.hsn-table tfoot tr {
  break-inside: avoid;
  page-break-inside: avoid;
}
/* Spacer / filler rows: allow splitting only if needed (tall blank block) */
.items-table-blank-block tr,
.items-table-spacer tr {
  break-inside: auto;
  page-break-inside: auto;
}

/* Keep summary + payment rows in tfoot together */
.items-tfoot-summary {
  break-inside: avoid;
  page-break-inside: avoid;
}
.items-table--invoice tfoot tr {
  break-inside: avoid;
  page-break-inside: avoid;
}

.items-table--invoice {
  table-layout: auto;
  width: 100%;
}
.items-table-wrap { margin: 0; background: #fff; overflow: visible; }
.items-table td, .items-table th { padding: 5px 6px; }
.items-table--invoice thead td,
.items-table--invoice thead th {
  background: #fff;
  font-weight: bold;
}

.company-logo { max-height: 56px; max-width: 120px; display: block; object-fit: contain; }
.company-header-with-logo { display: flex; align-items: flex-start; gap: 12px; }

/* HSN / GST summary — B&W, white cell fills (no gray) */
.gst-summary-table.hsn-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  table-layout: fixed;
  font-size: 10px;
  line-height: 1.2;
  background: #fff !important;
  color: #000 !important;
}
.gst-summary-table.hsn-table th,
.gst-summary-table.hsn-table td {
  border: 1px solid #000 !important;
  padding: 3px 4px;
  vertical-align: middle;
  background: #fff !important;
  color: #000 !important;
}
.gst-summary-table.hsn-table thead th {
  font-weight: bold;
  text-align: center;
  background: #fff !important;
  color: #000 !important;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.gst-summary-table.hsn-table .hsn-th-amt,
.gst-summary-table.hsn-table .hsn-th-sub {
  text-align: center;
}
.gst-summary-table.hsn-table .hsn-code,
.gst-summary-table.hsn-table .hsn-th-code,
.gst-summary-table.hsn-table .total-label {
  text-align: center;
}
.gst-summary-table.hsn-table .hsn-amt {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.gst-summary-table.hsn-table .hsn-rate {
  text-align: center;
}
.gst-summary-table.hsn-table tbody td:nth-child(2),
.gst-summary-table.hsn-table tbody td:nth-child(4),
.gst-summary-table.hsn-table tbody td:nth-child(6),
.gst-summary-table.hsn-table tbody td:nth-child(7) {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.gst-summary-table.hsn-table tbody td:nth-child(1),
.gst-summary-table.hsn-table tbody td:nth-child(3),
.gst-summary-table.hsn-table tbody td:nth-child(5) {
  text-align: center;
}
.gst-summary-table.hsn-table.hsn-table--igst tbody td:nth-child(2),
.gst-summary-table.hsn-table.hsn-table--igst tbody td:nth-child(4),
.gst-summary-table.hsn-table.hsn-table--igst tbody td:nth-child(5) {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.gst-summary-table.hsn-table thead { display: table-header-group; }
.gst-summary-table.hsn-table tfoot { display: table-footer-group; }
.gst-summary-table.hsn-table .gst-total-row td,
.gst-summary-table.hsn-table .total-row td {
  font-weight: bold;
  background: #fff !important;
  color: #000 !important;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.gst-summary-table.hsn-table .gst-total-row .hsn-amt,
.gst-summary-table.hsn-table .total-row .hsn-amt {
  text-align: right;
}
.gst-summary-table.hsn-table .gst-total-row .total-label {
  text-align: center;
}

.compact .gst-summary-table.hsn-table,
.invoice-container.compact .hsn-table {
  font-size: 9px;
}
.compact .gst-summary-table.hsn-table th,
.compact .gst-summary-table.hsn-table td,
.invoice-container.compact .hsn-table th,
.invoice-container.compact .hsn-table td {
  padding: 2px 4px;
}

.invoice-footer-table {
  width: 100%;
  table-layout: fixed;
}
.invoice-footer-td {
  width: 33.33%;
  border-top: none;
  border-right: 1px solid #000;
  border-bottom: none;
  border-left: none;
  padding: 4px 6px;
  font-size: 11px;
  line-height: 1.3;
  vertical-align: top;
  background: #fff;
  color: #000;
}
.invoice-footer-td:last-child { border-right: none; }
.invoice-footer-td-sign { text-align: center; }
.auth-signatory { font-size: 10px; margin-top: 2px; }

.items-table-spacer td {
  border: 1px solid #000 !important;
  padding: 0 6px !important;
  min-height: 22px !important;
  height: 22px !important;
  background: #fff !important;
  line-height: 0 !important;
  vertical-align: top !important;
  font-size: 0 !important;
}
.items-table-blank-block-cell {
  border-top: none !important;
  border-left: 1px solid #000 !important;
  border-right: 1px solid #000 !important;
  border-bottom: 1px solid #000 !important;
  padding: 0 !important;
  background: #fff !important;
  line-height: 0 !important;
  font-size: 0 !important;
  vertical-align: top !important;
}

.amounts-section { width: 100%; border-top: 1px solid #000; clear: both; }
.invoice-page { page-break-after: always; break-after: page; }
.invoice-page:last-child { page-break-after: auto; break-after: auto; }
.continued-msg { text-align: center; padding: 4px 0; font-size: 11px; color: #000; }
.tax-invoice-title { text-align: center; font-size: 16px; font-weight: bold; padding: 8px 0; border-bottom: 1px solid #000; margin-bottom: 4px; }

.footer-signature-img { max-height: 36px; max-width: 120px; object-fit: contain; display: block; margin: 2px auto; }
.signature-line { border-bottom: 1px solid #000; width: 120px; margin: 4px auto 2px; height: 0; line-height: 0; }
.amounts-cell { border: none !important; }
.items-table .amounts-cell { border: 1px solid #000 !important; }
.amounts-cell .no-border td { border: none !important; }
.payment-mode-cell { padding-top: 6px; border-top: 1px solid #000; }
.summary-quad { vertical-align: top; }
.summary-amounts-mini td { padding: 2px 0; }
.footer-for-company { text-align: right; width: 100%; margin-bottom: 2px; }
/* Ensure signature block is centered inside signature cell */
.invoice-footer-td-sign .footer-for-company { text-align: center; }
</style>
</head>
<body>
<div class="main">
${mainContent}
</div>
</body>
</html>`;
}

/**
 * Generate bill PDF, upload to backend, update bill. Returns fileUrl.
 * @param {string} billId
 * @returns {Promise<{fileUrl: string}>}
 */
export async function generateBillPdf(billId) {
  const res = await billService.getBillById(billId);
  const bill = res?.bill ?? res;
  if (!bill || !bill._id) throw new Error("Bill not found");
  if (!(bill.items || []).length) throw new Error("Bill has no items");

  let business = null;
  try {
    const br = await businessService.getMyBusiness();
    business = br?.business ?? null;
  } catch (e) {
    console.warn("Business fetch failed:", e);
  }

  let signatureDataUrl = null;
  const rawSigUrl = business?.authorizedSignature?.url;
  if (rawSigUrl) {
    signatureDataUrl = await fetchImageAsDataUrl(rawSigUrl);
  }

  const invoiceHTML = generateInvoiceHTML(bill, business, { signatureDataUrl });
  const parser = new DOMParser();
  const doc = parser.parseFromString(invoiceHTML, 'text/html');
  const parserError = doc.querySelector('parsererror');
  if (parserError) throw new Error('Failed to parse HTML');

  const bodyContent = doc.body ? doc.body.innerHTML : '';
  const styles = doc.head ? (doc.head.querySelector('style')?.innerHTML || '') : '';
  if (!bodyContent) throw new Error('No body content');

  const a4WidthPx = 794;
  const tempDiv = document.createElement('div');
  // Far off-screen but opacity:1 — html2canvas often paints blank/half glyphs when parent has opacity:0
  tempDiv.style.cssText =
    'position:fixed;left:-10000px;top:0;width:' +
    a4WidthPx +
    'px;min-height:100px;max-height:none;background:#fff;padding:0;margin:0;overflow:visible;opacity:1;pointer-events:none;z-index:-1;box-sizing:border-box';
  document.body.appendChild(tempDiv);

  if (styles) {
    const styleEl = document.createElement('style');
    styleEl.textContent = styles;
    tempDiv.appendChild(styleEl);
  }
  tempDiv.insertAdjacentHTML('beforeend', bodyContent);

  void tempDiv.offsetHeight;
  await new Promise((r) => setTimeout(r, 150));
  void tempDiv.offsetHeight;
  await new Promise((r) => setTimeout(r, 250));

  const mainDiv = tempDiv.querySelector('.main') || tempDiv.firstElementChild || tempDiv;
  if (!mainDiv || !mainDiv.innerHTML.trim()) {
    document.body.removeChild(tempDiv);
    throw new Error('No content for PDF');
  }

  void mainDiv.offsetHeight;

  const opt = {
    margin: [12, 12, 12, 12],
    filename: `${(bill.billNumber || 'bill').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDoc) => {
        const b = clonedDoc.body;
        if (b) {
          b.style.overflow = 'visible';
          b.style.height = 'auto';
        }
        const clonedMain = clonedDoc.querySelector('.main');
        if (clonedMain) {
          clonedMain.style.overflow = 'visible';
          clonedMain.style.height = 'auto';
          clonedMain.style.minHeight = '0';
        }
        clonedDoc.querySelectorAll('.bottom-section, .gst-footer-cluster, .gst-summary-table.hsn-table').forEach((el) => {
          el.style.overflow = 'visible';
          el.style.maxHeight = 'none';
        });
      },
    },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
    /* Avoid 'table' — it breaks multi-row HSN/GST summary in PDF (rows get clipped). */
    pagebreak: { mode: ['css', 'legacy'], before: '.page-break-before', after: '.page-break-after', avoid: ['tr'] },
  };

  const pdfBlob = await html2pdf().set(opt).from(mainDiv).outputPdf('blob');
  document.body.removeChild(tempDiv);

  const pdfFileName = `${(bill.billNumber || 'bill').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  const pdfFile = new File([pdfBlob], pdfFileName, { type: "application/pdf" });

  const uploadFormData = new FormData();
  uploadFormData.append("pdfFile", pdfFile);
  uploadFormData.append("billId", bill._id);
  uploadFormData.append("fileName", bill.billNumber);
  uploadFormData.append("fileSize", pdfFile.size.toString());
  uploadFormData.append("imageCount", String((bill.items || []).length));
  uploadFormData.append("description", `Bill: ${bill.billNumber}`);

  const backendOrigin = whatsappApi.defaults.baseURL
    ? new URL(whatsappApi.defaults.baseURL).origin
    : (typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL) || "";
  const uploadUrl = backendOrigin ? `${backendOrigin.replace(/\/$/, '')}/api/pdfs` : "/api/pdfs";

  const uploadResponse = await axios.post(uploadUrl, uploadFormData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
    withCredentials: true,
  });

  const pdf = uploadResponse.data?.pdf;
  if (!pdf?.fileUrl) throw new Error("PDF upload failed");

  const fileUrl = pdf.fileUrl;
  const filePath = fileUrl.replace(/^\//, "");

  await billService.updateBill(bill._id, {
    pdfFile: {
      fileName: pdf.fileName || undefined,
      filePath,
      fileUrl,
    },
  });

  return { fileUrl };
}
